import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  PhoneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import Logo from './Logo';
import Navigation from './Navigation';
import turnstileConfig from '../config/turnstile';
import googleConfig from '../config/google';
import config from '../config/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const { login, loginWithToken, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle Google OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Đăng nhập Google thất bại. Vui lòng thử lại.');
      return;
    }

    if (token) {
      // Save token and redirect
      localStorage.setItem('token', token);
      if (loginWithToken) {
        loginWithToken(token);
      }
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate, loginWithToken]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Load Turnstile
  useEffect(() => {
    if (user) return;

    const loadTurnstile = () => {
      if (!document.getElementById('turnstile-script')) {
        const script = document.createElement('script');
        script.id = 'turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        document.head.appendChild(script);
        script.onload = renderTurnstile;
      } else if (window.turnstile) {
        renderTurnstile();
      }
    };
    const renderTurnstile = () => {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileConfig.siteKey,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'dark',
          language: 'vi'
        });
      }
    };
    loadTurnstile();
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [user]);

  // Load Google Sign-In SDK
  useEffect(() => {
    const loadGoogleScript = () => {
      if (!document.getElementById('google-signin-script')) {
        const script = document.createElement('script');
        script.id = 'google-signin-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        script.onload = initializeGoogle;
      } else if (window.google) {
        initializeGoogle();
      }
    };

    const initializeGoogle = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: googleConfig.clientId,
          callback: handleGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
      }
    };

    loadGoogleScript();
  }, []);

  const handleGoogleCredential = async (response) => {
    if (response.credential) {
      setGoogleLoading(true);
      setError('');

      try {
        const apiResponse = await fetch(`${config.TASKS_API_URL}/auth/google/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ credential: response.credential }),
        });

        const data = await apiResponse.json();

        if (apiResponse.ok && data.access_token) {
          localStorage.setItem('token', data.access_token);
          if (loginWithToken) {
            loginWithToken(data.access_token);
          }
          navigate('/dashboard', { replace: true });
        } else {
          setError(data.detail || 'Đăng nhập Google thất bại');
        }
      } catch (err) {
        console.error('Google login error:', err);
        setError('Lỗi kết nối. Vui lòng thử lại.');
      } finally {
        setGoogleLoading(false);
      }
    }
  };

  const handleGoogleLogin = () => {
    if (window.google) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: redirect to Google OAuth
          window.location.href = `${config.TASKS_API_URL}/auth/google/login`;
        }
      });
    } else {
      // Fallback: redirect to Google OAuth
      window.location.href = `${config.TASKS_API_URL}/auth/google/login`;
    }
  };

  const resetTurnstile = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken('');
    }
  };

  if (user) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) { setError("Vui lòng xác minh bạn không phải robot"); return; }
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password, turnstileToken);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || "Đăng nhập thất bại"); resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <Navigation />

      <div className="flex pt-16">
        {/* Left side - Login Form */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 bg-dark-900">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            {/* Logo and Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <Logo size="md" />
              </div>

              <h2 className="text-3xl font-bold text-white mb-2">
                Chào mừng trở lại!
              </h2>
              <p className="text-sm text-ocean-100/60 mb-8">
                Đăng nhập vào Portal Khách hàng để quản lý dự án, hợp đồng và dịch vụ của bạn
              </p>
            </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ocean-100/80 mb-2">
                  Địa chỉ Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-cyan-400/50" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 bg-dark-800/50 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-200"
                    placeholder="your@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-ocean-100/80 mb-2">
                  Mật khẩu
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-cyan-400/50" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-12 py-3 bg-dark-800/50 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-200"
                    placeholder="Nhập mật khẩu của bạn"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-cyan-400/50 hover:text-cyan-400 transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-cyan-400/50 hover:text-cyan-400 transition-colors" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Turnstile Widget */}
            <div className="flex justify-center my-4">
              <div ref={turnstileRef}></div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !turnstileToken}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-glow-cyan/30 hover:shadow-glow-cyan"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Đang đăng nhập...
                  </div>
                ) : (
                  'Đăng nhập'
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-cyan-500/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-dark-900 text-ocean-100/50">Hoặc</span>
              </div>
            </div>
          </div>

          {/* Google Login */}
          <div className="mt-6">
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-cyan-500/20 rounded-xl text-sm font-medium text-ocean-100/80 bg-dark-800/50 hover:bg-cyan-500/10 hover:border-cyan-500/40 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Đang đăng nhập...
                </div>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Đăng nhập bằng Google
                </>
              )}
            </button>
          </div>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-ocean-100/60">
              Chưa có tài khoản?{' '}
              <Link to="/register" className="font-medium text-cyan-400 hover:text-cyan-300 transition-colors">
                Đăng ký ngay
              </Link>
            </p>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 pt-6 border-t border-cyan-500/10">
            <div className="flex items-center justify-center space-x-6 text-sm text-ocean-100/50 mb-6">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-4 w-4 mr-1 text-cyan-400" />
                <span>Bảo mật</span>
              </div>
              <div className="flex items-center">
                <GlobeAltIcon className="h-4 w-4 mr-1 text-cyan-400" />
                <span>Đáng tin cậy</span>
              </div>
            </div>

            {/* Company Info & Links */}
            <div className="text-center space-y-4">
              <div className="text-sm text-ocean-100/50">
                <p className="font-medium text-white mb-2"><span className="text-white">SMART</span><span className="text-cyan-400">ERA</span></p>
                <p className="mb-2">Giải pháp công nghệ thông minh</p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs">
                  <div className="flex items-center text-cyan-400/60">
                    <EnvelopeIcon className="h-3 w-3 mr-1" />
                    <span>smarteravn@gmail.com</span>
                  </div>
                  <div className="flex items-center text-cyan-400/60">
                    <PhoneIcon className="h-3 w-3 mr-1" />
                    <span>0903954852</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                <a
                  href="https://smartera.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-cyan-500/20 rounded-lg text-sm font-medium text-ocean-100/70 hover:text-cyan-400 hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors"
                >
                  <GlobeAltIcon className="h-4 w-4 mr-2" />
                  Trang chủ Smartera
                </a>

                <a
                  href="https://smartera.uk/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Liên hệ hỗ trợ
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Hero Section */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-800 via-cyan-900/30 to-blue-900/30">
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>

          {/* Content */}
          <div className="relative h-full flex flex-col justify-center px-12">
            <div className="max-w-lg">
              <h1 className="text-4xl font-bold text-white mb-6">
                <span className="text-white">SMART</span><span className="text-cyan-400">ERA</span> Portal
                <span className="block text-cyan-400/80 text-2xl mt-2">Quản lý dự án thông minh</span>
              </h1>

              <p className="text-xl text-ocean-100/70 mb-8">
                Nền tảng quản lý dự án và dịch vụ công nghệ hàng đầu. Theo dõi tiến độ, quản lý website một cách hiệu quả với công nghệ AI tiên tiến.
              </p>

              {/* Features */}
              <div className="space-y-4">
                {[
                  'Phát triển ứng dụng Web & Mobile',
                  'Giải pháp AI & Machine Learning',
                  'Dịch vụ Cloud & DevOps',
                  'Tư vấn chuyển đổi số'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center text-ocean-100/80">
                    <div className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 border border-cyan-500/30 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-6">
                {[
                  { value: '150+', label: 'Dự án' },
                  { value: '80+', label: 'Khách hàng' },
                  { value: '8+', label: 'Năm kinh nghiệm' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-cyan-400 font-mono">{stat.value}</div>
                    <div className="text-sm text-ocean-100/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-cyan-500/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-blue-500/10 rounded-full blur-lg"></div>

          {/* Company footer */}
          <div className="absolute bottom-6 left-12 right-12">
            <div className="flex items-center justify-between text-ocean-100/50 text-sm">
              <div>
                <p className="font-medium">© 2017-2025 <span className="text-white">SMART</span><span className="text-cyan-400">ERA</span></p>
                <p className="text-ocean-100/40">Thành lập từ 2017 - Công nghệ thông minh cho tương lai</p>
              </div>
              <div className="flex flex-wrap space-x-4">
                <a href="https://smartera.uk" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                  Website
                </a>
                <a href="https://giaiphapthietkeweb.com" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                  Blog
                </a>
                <a href="https://urlsimplify.com" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                  URL Simplify
                </a>
                <a href="tel:0935894211" className="hover:text-cyan-400 transition-colors">
                  0935 894 211
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Login;
